import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import {
    IsActiveMatchOptions,
    NavigationEnd,
    Router,
    RouterModule,
} from '@angular/router';
import { EnvironmentService } from '@core/services/environment.service';
import { QuicklinkModule } from 'ngx-quicklink';
import { Subject, filter, takeUntil } from 'rxjs';

const CURRENT_DOMAIN_NAME = 'followalice.com';

@Component({
    selector: 'app-link',
    templateUrl: './link.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, RouterModule, QuicklinkModule],
    host: { ngSkipHydration: 'true' },
})
export class LinkComponent implements OnInit, OnChanges, OnDestroy {
    @Input() link: string;
    @Input() title: string;
    @Input() additionalClasses: string[] | string;
    @Input() routerLinkActive: string[] | string = '';
    @Input() routerLinkInActive: string[] | string = '';
    @Input() options: IsActiveMatchOptions = {
        fragment: 'ignored',
        matrixParams: 'ignored',
        paths: 'subset',
        queryParams: 'subset',
    };
    useRouterLink = false;
    externalLink = false;
    classes: string[] = [];

    isActive: boolean;
    destroy = new Subject();

    constructor(
        private environmentService: EnvironmentService,
        private router: Router,
        private cd: ChangeDetectorRef
    ) {
        this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                takeUntil(this.destroy)
            )
            .subscribe(() => {
                this.setLinkActive();
            });
    }

    ngOnInit() {
        this.isActive = this.router.isActive(this.link, this.options);
        this.cd.detectChanges();
    }

    setLinkActive() {
        this.isActive = this.router.isActive(this.link, this.options);
        this.classes = [
            ...this.prepareForClassMerging(this.additionalClasses),
            ...(this.isActive
                ? this.prepareForClassMerging(this.routerLinkActive)
                : this.prepareForClassMerging(this.routerLinkInActive)),
        ];
        this.cd.detectChanges();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes.link ||
            changes.routerLinkActive ||
            changes.routerLinkInActive ||
            changes.options ||
            changes.additionalClasses ||
            changes.isActive
        ) {
            this.setLinkActive();
        }

        if (changes.link && changes.link.currentValue) {
            let link = changes.link.currentValue;
            const baseURL = this.environmentService.getBaseLink();

            if (link.startsWith('www')) {
                link = `https://${link}`;
            }
            try {
                const urlObjectCurrent = new URL(baseURL);
                const urlObjectFromContentful = new URL(link, urlObjectCurrent);
                if (
                    urlObjectFromContentful.hostname.includes(
                        CURRENT_DOMAIN_NAME
                    )
                ) {
                    // if the hostname of a link includes followalice then it is a relative link
                    this.link = urlObjectFromContentful.pathname;

                    this.useRouterLink = true;
                } else if (
                    urlObjectFromContentful.origin !== urlObjectCurrent.origin
                ) {
                    // if the origin of both the links are different then it is an external link
                    this.externalLink = true;
                    this.link = link;
                } else {
                    // if the origin of both the links are same then it is a relative link

                    this.useRouterLink = true;
                }
            } catch (e) {
                // if the link is invalid then treat it as an external link
                this.externalLink = true;
                this.link = link;
            }

            this.cd.detectChanges();
        }
    }

    prepareForClassMerging(input: string[] | string): string[] {
        if (input === undefined || input === null) {
            return [];
        } else if (typeof input === 'string') {
            return input.split(' ');
        } else {
            return input.map(cssClass => cssClass.split(' ')).flat();
        }
    }

    ngOnDestroy(): void {
        this.destroy.next(null);
    }
}
